import { Phone, Mail, MapPin, ShieldCheck } from "lucide-react";
import { LogoBadge } from "./LogoBadge";
import { SocialIcons } from "./SocialIcons";
import { DEFAULT_WA_MESSAGE } from "../../lib/api";
import { useLang } from "../../lib/i18n";
import { useSettings, useContact } from "../../hooks/useSettings";

export const Footer = () => {
    const { t, fs } = useLang();
    const s = useSettings();
    const mobile = s.contact_mobile || "";
    const landline = s.contact_landline || "0112204999";
    const email = s.contact_email || "";
    const address = s.address_ar || t("ftr_addr");
    const { callUrl, waUrl } = useContact();

    return (
        <footer id="contact" className="relative border-t border-white/10 bg-[#070F26] pt-16 pb-8 overflow-hidden">
            <div className="max-w-7xl mx-auto px-4 sm:px-6">
                <div className="grid md:grid-cols-3 gap-10 mb-12">
                    <div className="space-y-4">
                        <LogoBadge />
                        <p className={`text-sm text-slate-400 leading-relaxed max-w-xs ${fs("ftr_tagline")}`}>
                            {t("ftr_tagline")}
                        </p>
                        <p className={`font-display font-bold text-amber-400/90 text-sm ${fs("hero_slogan")}`}>
                            {t("hero_slogan")}
                        </p>
                        <p className="font-mono-spec text-[10px] tracking-widest text-slate-600" dir="ltr">
                            WHOLESALE & RETAIL TIRES TRADING
                        </p>
                    </div>

                    <div className="space-y-3 text-sm">
                        <h4 className="font-display font-bold text-white mb-4">{t("ftr_contact")}</h4>
                        <a href={callUrl} className="flex items-center gap-3 text-slate-300 hover:text-amber-400 transition-colors duration-300" data-testid="footer-mobile-link">
                            <Phone className="w-4 h-4 text-amber-400" />
                            <span dir="ltr">{mobile}</span>
                        </a>
                        <a href={`tel:${landline}`} className="flex items-center gap-3 text-slate-300 hover:text-amber-400 transition-colors duration-300" data-testid="footer-landline-link">
                            <Phone className="w-4 h-4 text-amber-400" />
                            <span dir="ltr">{landline}</span>
                        </a>
                        <a href={`mailto:${email}`} className="flex items-center gap-3 text-slate-300 hover:text-amber-400 transition-colors duration-300" data-testid="footer-email-link">
                            <Mail className="w-4 h-4 text-amber-400" />
                            {email}
                        </a>
                        <p className="flex items-start gap-3 text-slate-400">
                            <MapPin className="w-4 h-4 text-amber-400 mt-1 shrink-0" />
                            {address}
                        </p>
                        <SocialIcons />
                    </div>

                    <div className="space-y-3 text-sm">
                        <h4 className="font-display font-bold text-white mb-4">{t("ftr_official")}</h4>
                        <p className="text-slate-400">
                            {t("ftr_cr")}{" "}
                            <span className="font-mono-spec text-slate-200" dir="ltr">7053792292</span>
                        </p>
                        <p className="text-slate-400">
                            {t("ftr_vat")}{" "}
                            <span className="font-mono-spec text-slate-200" dir="ltr">314664394700003</span>
                        </p>
                        <p className="text-slate-400">
                            {t("ftr_postal")}{" "}
                            <span className="font-mono-spec text-slate-200" dir="ltr">12644</span>
                        </p>
                        <p
                            data-testid="footer-saso-badge"
                            className="flex items-center gap-2 text-emerald-400 font-bold text-xs bg-emerald-500/10 border border-emerald-500/30 rounded-xl px-4 py-2.5 w-fit"
                        >
                            <ShieldCheck className="w-4 h-4 shrink-0" />
                            {t("ftr_saso")}
                        </p>
                        <a
                            href={waUrl(DEFAULT_WA_MESSAGE)}
                            target="_blank"
                            rel="noreferrer"
                            data-testid="footer-whatsapp-button"
                            className="inline-block mt-3 bg-emerald-500 text-[#0A1228] text-xs font-extrabold rounded-full px-6 py-2.5 hover:bg-emerald-400 transition-colors duration-300"
                        >
                            {t("ftr_cta")}
                        </a>
                    </div>
                </div>

                <div className="border-t border-white/5 pt-6 flex flex-col sm:flex-row items-center justify-between gap-3">
                    <p className={`text-xs text-slate-600 ${fs("ftr_rights")}`}>
                        {t("ftr_rights")}
                    </p>
                    <p className="font-mono-spec text-[10px] tracking-[0.3em] text-slate-700" dir="ltr">
                        AL THURAYA TIRE AUTO SERVICES CO
                    </p>
                </div>
            </div>
        </footer>
    );
};
